import { FC, useRef, useState, useEffect, MutableRefObject } from 'react';
import clsx from 'clsx';

import { Button } from '../../atoms/Button';
import { Heading } from '../../atoms/Heading';
import { Icon } from '../../atoms/Icon';
import { headingType, size } from '../../utils/enums/enums';
import { GeneralMessageProps } from './GeneralMessage.types';
import { StyledGeneralMessage } from './GeneralMessage.styles';
import { RichText } from '../RichText';

export const GeneralMessage: FC<GeneralMessageProps> = props => {
  const {
    openInNewTab = false,
    header,
    headerTag,
    descriptionText,
    linkText,
    ctaType,
    linkUrl,
    trackingProperties,
    styleclass,
    componentId,
    ARIA_COLLAPSE,
    ARIA_EXPAND,
    refCallback,
  } = props;

  const GeneralMessageRef = useRef(null) as unknown as MutableRefObject<HTMLInputElement>;
  const buttonCallback = () => {
    window.open(linkUrl, openInNewTab ? '_blank' : '_self');
  };

  const customAttributes = trackingProperties?.enableScrollingBehavior
    ? { 'data-section-tracking': props?.componentId }
    : {};

  const tertiaryBtnClass =
    ctaType === 'tertiarylink'
      ? openInNewTab
        ? 'm-link-tertiary-button-inverse m-link-tertiary-button-external gm_link'
        : 'm-link-tertiary-button-inverse gm_link'
      : '';
  const secondaryBtnClass =
    ctaType === 'secondarybutton'
      ? openInNewTab
        ? 'm-button-s m-button-secondary-inverse m-button-external'
        : 'm-button-s m-button-secondary-inverse'
      : '';
  const primaryBtnClass =
    ctaType === 'primarybutton'
      ? openInNewTab
        ? 'm-button-s m-button-primary-inverse m-button-external'
        : 'm-button-s m-button-primary-inverse'
      : '';

  const [isCollapsed, setIsCollapsed] = useState('d-block');
  const [iconClass, setIconClass] = useState('icon-arrow-up');
  const [display, setDisplay] = useState(true);
  const [showEllipsis, setShowEllipsis] = useState('');
  const [is1Line, setIs1Line] = useState(false);
  const [ariaLabel, setAriaLabel] = useState(ARIA_COLLAPSE);
  const outerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [previousRef, setPreviousRef] = useState<MutableRefObject<HTMLDivElement>>(outerRef);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isTextWrappedToTwoLines(element: any, spaceHeight: number) {
    if (element) {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const containerHeight = element.clientHeight - spaceHeight;
      return containerHeight / lineHeight;
    }
    return 1;
  }

  useEffect(() => {
    const headerContainer = document.querySelector(`.gmhead_${componentId}`);
    const descContainer = document.querySelector(`.gmdesc_${componentId}`);
    const headerLines = isTextWrappedToTwoLines(headerContainer, 0);
    const descLines = isTextWrappedToTwoLines(descContainer, 16);
    const noOfLines = headerLines === 1 && descLines === 1;
    setIs1Line(noOfLines);
  }, []);

  // fire the refCallback when it's collapsed/expanded
  useEffect(() => {
    if (!outerRef.current) {
      return;
    }
    refCallback(outerRef.current);
  }, [display, refCallback]);

  // fire the refCallback if the ref changes
  useEffect(() => {
    if (outerRef.current !== previousRef?.current) {
      refCallback(outerRef.current);
      setPreviousRef(outerRef);
    }
  }, [outerRef, previousRef, refCallback]);

  // fire the refCallback if attributes are changed on the wrapper element
  // target will add/remove the `d-none` class arbitrarily
  useEffect(() => {
    if (!outerRef.current) {
      return;
    }
    const observer = new MutationObserver(() => refCallback(outerRef.current));
    observer.observe(outerRef.current, { attributes: true });
    return () => observer?.disconnect();
  }, [outerRef, refCallback]);

  const iconClickHandler = () => {
    setIsCollapsed(display ? 'd-none' : 'd-block');
    setIconClass(display ? 'icon-arrow-down' : 'icon-arrow-up');
    setShowEllipsis(display ? 'm-ellipsis-1line' : '');
    setAriaLabel(display ? ARIA_EXPAND : ARIA_COLLAPSE);
    setDisplay(!display);
  };

  return (
    <StyledGeneralMessage
      data-component-name="m-ui-library-GeneralMessage"
      data-testid="ui-library-GeneralMessage"
      className={clsx('p-0 m-container-fullbleed', `${styleclass ? styleclass : ''}`)}
      styleclass={styleclass}
      ref={outerRef}
    >
      <div className={'gm container-sm'} data-testid="general-message" {...customAttributes}>
        {header && (
          <Heading
            element={headerTag}
            variation={headingType.subtitle}
            fontSize={size.large}
            titleText={header || ''}
            disableCustomClickTrack
            customClass={clsx('gm_text', 'gm_heading', showEllipsis, `gmhead_${componentId}`)}
          />
        )}
        {descriptionText && (
          <RichText
            text={descriptionText}
            customClass={clsx(
              'gm_text',
              'gm_description',
              'mx-auto',
              't-font-s',
              showEllipsis,
              `gmdesc_${componentId}`
            )}
            componentId={componentId + '-DescriptionText'}
          />
        )}
        {linkText && linkUrl && (
          <Button
            href={linkUrl}
            className={clsx('gm_button', primaryBtnClass, secondaryBtnClass, tertiaryBtnClass, isCollapsed)}
            target={`${openInNewTab ? '_blank' : '_self'}`}
            linkType={`${openInNewTab ? 'external' : ''}`}
            rel={`${openInNewTab ? 'noopener noreferrer' : ''}`}
            ref={GeneralMessageRef}
            buttonCopy={linkText}
            trackingProperties={trackingProperties}
            isTextBeforeChildren={false}
            callback={buttonCallback}
          />
        )}
        {(linkText || (!linkText && !is1Line)) && (
          <div className={clsx('gm_collapse')} onClick={iconClickHandler} tabIndex={0}>
            <Icon iconClass={clsx('icon', `${iconClass}`)}>
              <span className="visually-hidden" aria-live="polite">
                {ariaLabel}
              </span>
            </Icon>
          </div>
        )}
      </div>
    </StyledGeneralMessage>
  );
};
